import React, { useEffect } from "react";
import "./style.scss";
import { Link } from "react-router-dom";
import {
  scrollToTop,
  stressops_hero1,
  stressops_hero2,
  stressops_hero1_mob,
  stressops_hero2_mob,
  stressops_logo,
} from "src/shared/utils/appConstant";
import { pageRoutes } from "src/shared/utils/pageRoutes.constant";
import HomePageFooter from "src/views/home/components/Footer";
import Footer from "src/components/footer";

const DocumentIcon = ({ color }: { color: string }) => (
  <svg width="13" height="15" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2 1H8.5L12 4.5V14.5C12 14.7761 11.7761 15 11.5 15H2.5C2.22386 15 2 14.7761 2 14.5V1Z"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 1V5H12"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StressOps: React.FC = () => {

  useEffect(() => {
    scrollToTop();
  }, []);

  const featureCards = [
    {
      num: "01",
      title: "Guided Test Creation",
      desc: "Configure production-quality tests using presets — no performance engineering background required.",
      bullets: [
        "Load, stress, spike, and soak presets with traffic and environment tabs.",
        "Optional dry-run before full-scale execution.",
      ],
    },
    {
      num: "02",
      title: "Multi-Step API Workflows",
      desc: "Chain real user journeys — login, token reuse, follow-up calls — under load.",
      bullets: [
        "Variable extraction, retries, and shared origins/tokens on authentication.",
        "Validates the paths users actually take, not just isolate endpoints.",
      ],
    },
    {
      num: "03",
      title: "Real-time Visibility",
      desc: "Watch a test as it happens, not after it finishes.",
      bullets: [
        "Live VUs, RPS, latency percentiles, errors, and worker status.",
        "Stop unsafe runs before they overwhelm shared environments.",
      ],
    },
    {
      num: "04",
      title: "Distributed Execution",
      desc: "Scale test throughput by adding workers, without redesigning anything.",
      bullets: [
        "Redis-backed job queues across one or more workers.",
        "Parallel test capacity grows with demand.",
      ],
    },
    {
      num: "05",
      title: "History, Comparison & Reporting",
      desc: "Turn every run into evidence teams can see and share.",
      bullets: [
        "Side-by-side run comparison against a baseline.",
        "Human-readable PDF reports plus JSON and CSV exports.",
      ],
    },
    {
      num: "06",
      title: "Automation & Reuse",
      desc: "Make tests repeatable assets instead of one-off exercises.",
      bullets: [
        "API collections, environments, and CSV data feeding.",
        "Postman/Swagger/cURL import and scheduled re-runs.",
      ],
    },
  ];

  const processCards = [
    {
      stepTag: "STEP 01 // LOAD",
      num: "01",
      title: "Define Profile",
      desc: "Declare target APIs (single URL or multi-step journeys) with a targeted traffic profile, auth setups, and dynamic CSV data imports.",
      previewType: "schemas",
    },
    {
      stepTag: "STEP 02 // ENGINE",
      num: "02",
      title: "Validate & Queue",
      desc: "StressOps analyzes setups against production safety caps, auto-generates highly optimized k6 scripts, and queues the job.",
      previewType: "safety_code",
    },
    {
      stepTag: "STEP 03 // STREAM",
      isLive: true,
      num: "03",
      title: "Stream Telemetry",
      desc: "Watch Virtual Users (VUs), latency percentiles, RPS, and errors drop in real time. Stop dangerous runs instantly with a single click.",
      previewType: "telemetry_abort",
    },
    {
      stepTag: "STEP 04 // EVIDENCE",
      num: "04",
      title: "Sign-Off Evidence",
      desc: "Compare run signatures against baseline runs instantly. Generate compliance-approved PDF and structured JSON reports for release review.",
      previewType: "evidence_report",
    },
  ];

  const statusQuoItems = [
    {
      title: "External SaaS or Ad-Hoc Scripts",
      desc: "Teams wrestle with third-party cloud tools that leak test data, or maintain fragile, undocumented k6 scripts locally.",
    },
    {
      title: "Specialist Bottleneck",
      desc: "Only dedicated performance engineers have the expertise to configure complex virtual user (VU) scenarios and infrastructure parameters.",
    },
    {
      title: "Blind Spot Journeys",
      desc: "Hitting single REST endpoints misses real-world multi-step user behavior, leaving complex transaction database locks undetected.",
    },
    {
      title: "Zero Run-Time Visibility",
      desc: "Once a script is launched, you fly blind for hours. There is no real-time telemetry to abort early if database locks occur.",
    },
    {
      title: "Scattered, Static Reports",
      desc: "Results live in disconnected CSV logs, Markdown files, or slack screenshots. Tracking regression baselines is manually impossible.",
    },
  ];

  const confidentDeliveryItems = [
    {
      title: "Native Internal Platform",
      desc: "Run safe, authenticated load tests inside your secure VPC. Keeps your environment and staging databases private.",
    },
    {
      title: "Preset-Driven Setup",
      desc: "Zero k6 experience required. Use smart GUI templates for common user patterns, instant spike tests, or soak runs.",
    },
    {
      title: "Complex Multi-Step Flows",
      desc: "Replicate authentic end-to-end user lifecycles: Login, browse catalogue, add-to-cart, pay, checkout, and logout.",
    },
    {
      title: "Live Interactive Telemetry",
      desc: "Watch requests, error rates, and latency percentiles stream live. Instantly abort or tweak load using emergency controls.",
    },
    {
      title: "Integrated Performance Intelligence",
      desc: "Auto-compare against historical baselines, save regression tests, and export high-fidelity PDF/Excel reports for stakeholders.",
    },
  ];

  return (
    <div className="stressops-container animate__animated animate__fadeIn">
      {/* 1. Hero Section */}
      <section className="stressops-hero">
        <div className="hero-backdrop-pillar" />
        <div className="hero-content-left animate__animated animate__fadeInLeft">
          <h1 className="hero-title">StressOps</h1>

          <p className="hero-subtitle">
            StressOps is a distributed API performance and load testing platform that helps engineering teams validate scalability, simulate real-world traffic, and capture release-ready evidence — all from one centralized system.
          </p>
        </div>

        {/* Right Side StressOps Official Logo */}
        <div className="hero-chevron-right animate__animated animate__fadeInRight">
          <img src={stressops_logo} alt="StressOps" className="stressops-hero-logo" />
        </div>

        {/* Breadcrumb Navigation */}
        <div className="hero-breadcrumb">
          <Link to={pageRoutes.Home} onClick={scrollToTop}>
            Home
          </Link>
          <span className="breadcrumb-sep">/</span>
          <Link to={pageRoutes.Marketplace} onClick={scrollToTop}>
            Marketplace
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-active">StressOps</span>
        </div>
      </section>

      {/* 2. "What is StressOps?" Section */}
      <section className="what-is-stressops-section">
        <div className="what-is-container">
          <div className="what-is-text animate__animated animate__fadeInUp">
            <div className="what-is-header-row">
              <div className="title-grey-box" aria-hidden="true" />
              <h2 className="outline-title">
                What is
                <br />
                <span className="stressops-outline-row">
                  StressOps?
                  <span className="mobile-dots" aria-hidden="true">
                    <span className="dot dot-red" />
                    <span className="dot dot-blue" />
                    <span className="dot dot-red" />
                  </span>
                </span>
              </h2>
            </div>

            {/* In mobile, mockup is displayed between title and description */}
            <div className="what-is-mockup-mobile">
              <div className="stressops-layered-mockup">
                <object data={stressops_hero1_mob} type="image/svg+xml" aria-label="StressOps Dashboard Overview" className="stressops-mockup-bg" style={{ pointerEvents: 'none' }} />
                <object data={stressops_hero2_mob} type="image/svg+xml" aria-label="StressOps Test Detail" className="stressops-mockup-fg" style={{ pointerEvents: 'none' }} />
              </div>
            </div>

            <p className="what-is-p">
              We built <strong className="highlight-brand">StressOps</strong> so teams turn business goals — load, stress, spike, soak — into executable k6 tests through a guided UI, run them on a scalable worker pool, and watch live metrics stream in as the test runs. Results, comparisons, and PDF reports are retained for every release.
            </p>
            <p className="what-is-p">
              StressOps is built for engineering teams, QA and performance testers, engineering managers, platform/DevOps teams, and API owners who need reliable performance validation without external tool dependency.
            </p>
          </div>

          <div className="what-is-mockup animate__animated animate__fadeInRight">
            <div className="stressops-layered-mockup">
              <img src={stressops_hero1} alt="StressOps Dashboard Overview" className="stressops-mockup-bg" />
              <img src={stressops_hero2} alt="StressOps Test Detail" className="stressops-mockup-fg" />
            </div>
          </div>
        </div>
      </section>

      {/* 3. "Guided, Distributed API Performance Testing — Without the k6 Learning Curve" Section */}
      <section className="guided-testing-section">
        <div className="guided-header">
          <h2 className="guided-title">
            Guided, Distributed API Performance Testing —{" "}
            <span className="highlight-indigo">Without the k6 Learning Curve</span>
          </h2>
          <p className="guided-desc">
            Simplify API performance testing with guided workflows, distributed execution, real-time insights, and reusable test automation — without the complexity of learning k6.
          </p>
        </div>

        <div className="guided-cards-grid">
          {featureCards.map((card, idx) => (
            <div className="guided-feature-card" key={idx}>
              <div className="card-num">{card.num}</div>
              <h3 className="card-heading">{card.title}</h3>
              <p className="card-desc">{card.desc}</p>
              {card.bullets.length > 0 && (
                <ul className="card-bullets">
                  {card.bullets.map((bullet, bIdx) => (
                    <li key={bIdx}>{bullet}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* 4. "From Test Goal to Release-Ready Evidence" Section */}
      <section className="process-flow-section">
        <div className="section-header">
          <h2 className="section-title">
            From Test Goal to <span className="highlight-indigo">Release-Ready Evidence</span>
          </h2>
        </div>

        <div className="process-cards-grid">
          {processCards.map((card, idx) => (
            <div className="process-card" key={idx}>
              <div className="process-card-header">
                <span className="process-step-tag">{card.stepTag}</span>
                {card.isLive ? (
                  <span className="process-live-tag">
                    <span className="live-dot" /> LIVE
                  </span>
                ) : (
                  <span className="process-num">{card.num}</span>
                )}
              </div>

              <div className="process-card-body">
                <h3 className="process-title">{card.title}</h3>
                <p className="process-desc">{card.desc}</p>
              </div>

              <div className="process-preview-box">
                {card.previewType === "schemas" && (
                  <div className="preview-schemas">
                    <div className="schemas-label">IMPORT SCHEMAS:</div>
                    <div className="schemas-list">
                      <div className="schema-pill">
                        <DocumentIcon color="#a855f7" />
                        <span>POSTMAN</span>
                      </div>
                      <div className="schema-pill">
                        <DocumentIcon color="#10b981" />
                        <span>SWAGGER</span>
                      </div>
                      <div className="schema-pill">
                        <DocumentIcon color="#475569" />
                        <span>HAR ARCS</span>
                      </div>
                    </div>
                  </div>
                )}

                {card.previewType === "safety_code" && (
                  <div className="preview-safety-code">
                    <div className="safety-header">
                      <span className="safety-title">✓ SAFETY CAP CHECK</span>
                      <span className="safety-badge">PASS</span>
                    </div>
                    <div className="code-box">
                      <div className="code-filename">stressops_gen_k6.js</div>
                      <div className="code-line">
                        <span className="code-kw">import</span> http{" "}
                        <span className="code-kw">from</span>{" "}
                        <span className="code-str">'k6/http'</span>;
                      </div>
                      <div className="code-line">
                        <span className="code-kw">export const</span> options = &#123; v...
                      </div>
                    </div>
                  </div>
                )}

                {card.previewType === "telemetry_abort" && (
                  <div className="preview-telemetry-abort">
                    <div className="telemetry-stats">
                      <span className="stat-vus">VUs: 12,850</span>
                      <span className="stat-latency">p99: 42ms</span>
                    </div>
                    <div className="telemetry-bars">
                      <span className="bar bar-1" />
                      <span className="bar bar-2" />
                      <span className="bar bar-3" />
                      <span className="bar bar-4" />
                      <span className="bar bar-5" />
                      <span className="bar bar-6" />
                      <span className="bar bar-7" />
                    </div>
                    <button className="abort-btn" type="button">
                      ABORT RUN (CRITICAL)
                    </button>
                  </div>
                )}

                {card.previewType === "evidence_report" && (
                  <div className="preview-evidence-report">
                    <div className="report-header">
                      <span className="report-name">REPORT_#8024_PROD</span>
                      <span className="report-status">PASSED</span>
                    </div>
                    <div className="report-downloads">
                      <div className="download-pill">
                        <DocumentIcon color="#64748b" />
                        <span>PDF</span>
                      </div>
                      <div className="download-pill">
                        <DocumentIcon color="#64748b" />
                        <span>JSON</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 5. "From Guesswork to Measurable Performance Confidence" Section */}
      <section className="confidence-comparison-section">
        <div className="section-header">
          <h2 className="section-title">
            From Guesswork to <span className="highlight-indigo">Measurable</span><br />
            <span className="highlight-indigo">Performance Confidence</span>
          </h2>
        </div>

        <div className="confidence-cards-grid">
          {/* Card 1: The Status Quo Pain */}
          <div className="confidence-card status-quo-card">
            <div className="card-top-header">
              <h3 className="top-title">The Status Quo Pain</h3>
              <p className="top-subtitle">
                Fragile infrastructure, specialist silos, and a complete lack of operational clarity under pressure.
              </p>
            </div>

            <div className="confidence-items-list">
              {statusQuoItems.map((item, idx) => (
                <div className="confidence-item" key={idx}>
                  <div className="item-icon-circle quo-circle">
                    <span className="icon-inner">–</span>
                  </div>
                  <div className="item-text-content">
                    <h4 className="item-title">{item.title}</h4>
                    <p className="item-desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Card 2: Confident Delivery */}
          <div className="confidence-card confident-delivery-card">
            <div className="card-top-header">
              <h3 className="top-title green-title">Confident Delivery</h3>
              <p className="top-subtitle green-subtitle">
                Instant developer-guided workloads, streaming analytics, and automated baseline regression tracking.
              </p>
            </div>

            <div className="confidence-items-list">
              {confidentDeliveryItems.map((item, idx) => (
                <div className="confidence-item" key={idx}>
                  <div className="item-icon-circle confident-circle">
                    <span className="icon-inner">✓</span>
                  </div>
                  <div className="item-text-content">
                    <h4 className="item-title">{item.title}</h4>
                    <p className="item-desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6. Pre-Footer Contact Section */}
      <HomePageFooter page="StressOps" />

      {/* 8. Global Footer */}
      <Footer />
    </div>
  );
};

export default StressOps;
